import type { TopicDefinition, TopicStage } from "./topic-taxonomy";

export interface TopicStageCommunityContent {
  questionPrompt: string;
  experiencePrompt: string;
  primaryCta: string;
  emptyStateTitle: string;
  emptyStateDescription: string;
}

export interface TopicCommunityContent {
  topicId: string;
  intro: string;
  questionCta: string;
  experienceCta: string;
  emptyStateTitle: string;
  emptyStateDescription: string;
  stages: Record<string, TopicStageCommunityContent>;
}

/**
 * Produces practical Korean community copy from the managed Topic taxonomy.
 *
 * This is product copy, not fake community data. It never creates posts,
 * answers, experts, activity counts, or testimonials. Keeping it derived from
 * the taxonomy also prevents Topic/Stage copy from drifting away from the
 * canonical IDs used by metadata persistence.
 */
export function buildTopicCommunityContent(
  topic: TopicDefinition,
): TopicCommunityContent {
  return {
    topicId: topic.id,
    intro: `${topic.label}와 관련해 지금 겪고 있는 상황을 질문하거나, 직접 해결한 경험을 나눠보세요.`,
    questionCta: `${topic.label} 질문하기`,
    experienceCta: `${topic.label} 경험 나누기`,
    emptyStateTitle: `아직 ${topic.label} 질문이나 경험이 없습니다`,
    emptyStateDescription: `같은 상황에 있는 사람에게 도움이 되도록 첫 질문이나 실제 경험을 남겨보세요.`,
    stages: Object.fromEntries(
      topic.stages.map((stage) => [stage.id, buildStageCommunityContent(topic, stage)]),
    ),
  };
}

export function buildStageCommunityContent(
  topic: TopicDefinition,
  stage: TopicStage,
): TopicStageCommunityContent {
  return {
    questionPrompt: `${topic.label}에서 ‘${stage.label}’ 단계에 있습니다. 무엇이 가장 궁금하거나 막혀 있나요?`,
    experiencePrompt: `${topic.label}의 ‘${stage.label}’ 단계를 겪으면서 도움이 됐던 방법, 비용, 기간 또는 주의할 점을 나눠주세요.`,
    primaryCta: `${stage.label} 상황 남기기`,
    emptyStateTitle: `아직 ‘${stage.label}’ 단계의 이야기가 없습니다`,
    emptyStateDescription: `현재 상황을 질문하거나 해결 경험을 남기면 같은 단계의 사용자에게 먼저 연결됩니다.`,
  };
}
